import React, { useCallback, useEffect, useState } from 'react';
import {
  Form, Input, Modal, DatePicker, Radio, Typography, Row,
} from 'antd';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import ReactCountryFlag from 'react-country-flag';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import {
  selectEditState,
  selectNotificationsPageDataRequest,
  selectSubmitNotificationsRequest,
} from '../../../../state/ui/notification/NotificationUiReducer';
import { selectNotification } from '../../../../state/domain/notification/NotificationReducer';
import { cancelEditNotification, requestAddNotification, requestUpdateNotification } from '../../../../state/ui/notification/NotificationUiActions';
import { selectAppConfig } from '../../../../state/domain/appConfig/appConfigReducer';
import { requestSupportedLanguages } from '../../../../state/ui/application/sessionUiActions';

const { Text } = Typography;
const { TextArea } = Input;

const MESSAGE_MAX_CHARS = 400;

function range(start, end) {
  const result = [];
  for (let i = start; i < end; i += 1) {
    result.push(i);
  }
  return result;
}
// FIELDS
/**
 * The field for the message.  Allows clear, and max length set.  Required
 * The rules are injected into the form using the form field decorator.
 */
const MessageField = injectIntl(({
  form, locale, text, showLocale, intl, disabled,
}) => {
  const [charCount, setCharCount] = useState(text.length);

  return (
    <Form.Item style={{ display: locale === showLocale ? '' : 'none' }}>
      { form.getFieldDecorator(`message_${locale}`, {
        initialValue: text,
        rules: [
          { required: locale === 'en_GB', message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
        <span>
          <TextArea
            key={locale}
            style={{ marginTop: '0.3em' }}
            value={form.getFieldValue(`message_${locale}`)}
            autosize={{ minRows: 4, maxRows: 8 }}
            maxLength={MESSAGE_MAX_CHARS}
            disabled={disabled}
            placeholder={intl.formatMessage({ id: locale === 'en_GB' ? 'enter' : 'notifyPlaceholder' })}
            onChange={(e) => {
              setCharCount(e.target.value.length);
            }}
          />
          <Row type="flex" justify="end">
            <Text style={{ fontSize: '10pt' }}>
              <FormattedMessage id="textCharactersMax" values={{ max: MESSAGE_MAX_CHARS }} />
              {charCount}
            </Text>
          </Row>
        </span>,
      )}
    </Form.Item>
  );
});

/**
 * The field for the type.  Toggle (required))
 * The rules are injected into the form using the form field decorator.
 */
const TypeField = injectIntl(({ form, notification, disabled }) => (
  <Form.Item required label={<FormattedMessage id="type" />}>
    { form.getFieldDecorator('notificationType', {
      initialValue: notification ? notification.notificationType : 'info',
    })(
      <Radio.Group disabled={disabled} buttonStyle="solid">
        <Radio.Button value="info"><FormattedMessage id="info" /></Radio.Button>
        <Radio.Button value="warning"><FormattedMessage id="warning" /></Radio.Button>
      </Radio.Group>,
    )}
  </Form.Item>
));

/**
 * The fields for dates. Required and validated.
 * The rules are injected into the form using the form field decorator.
 */
const StartDateField = injectIntl(({
  form, notification, intl, disabled,
}) => {
  const currentEndDate = form.getFieldValue('endDateTime') && form.getFieldValue('endDateTime').clone().startOf('d');
  const currentStartDate = form.getFieldValue('startDateTime') && form.getFieldValue('startDateTime').clone().startOf('d');
  const startOfToday = moment().startOf('d');


  let disabledHours = [];
  let disabledMinutes = [];

  if (currentEndDate && currentStartDate && currentEndDate.isSame(currentStartDate) && form.getFieldValue('endDateTime').isAfter(moment())) {
    disabledHours = disabledHours.concat(range(form.getFieldValue('endDateTime').hours() + 1, 24));
    disabledMinutes = disabledMinutes.concat((form.getFieldValue('startDateTime').hours() === form.getFieldValue('endDateTime').hours()) ? range(form.getFieldValue('endDateTime').minutes(), 60) : []);
  }
  if (currentStartDate && startOfToday.isSame(currentStartDate)) {
    disabledHours = disabledHours.concat(range(0, moment().hours()));
    disabledMinutes = disabledMinutes.concat((form.getFieldValue('startDateTime').hours() === moment().hours()) ? range(0, moment().minutes() + 1) : []);
  }

  const disabledTime = {
    disabledHours: () => disabledHours,
    disabledMinutes: () => disabledMinutes,
  };

  return (
    <Form.Item required label={<FormattedMessage id="from" />}>
      { form.getFieldDecorator('startDateTime', {
        initialValue: notification ? moment(notification.startDateTime) : null,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
          {
            validator(rule, value, callback) {
              if (value && value.isBefore(moment()) && !disabled) {
                callback(intl.formatMessage({ id: 'startDateFuture' }));
              }
              callback();
            },
          },
        ],
      })(
        <DatePicker
          style={{ width: '300px' }}
          disabled={disabled}
          disabledTime={() => disabledTime}
          disabledDate={
            value => value.isBefore(startOfToday) || (currentEndDate ? value.isAfter(form.getFieldValue('endDateTime')) : false)
          }
          placeholder={intl.formatMessage({ id: 'selectOne' })}
          format="DD/MM/YYYY HH:mm"
          showTime={{ format: 'HH:mm' }}
        />,
      )}
    </Form.Item>
  );
});
const EndDateField = injectIntl(({
  form, notification, disabled, intl,
}) => {
  const currentEndDate = form.getFieldValue('endDateTime') && form.getFieldValue('endDateTime').clone().startOf('d');
  const currentStartDate = form.getFieldValue('startDateTime') && form.getFieldValue('startDateTime').clone().startOf('d');
  const startOfToday = moment().startOf('d');
  const startDateHour = form.getFieldValue('startDateTime') && form.getFieldValue('startDateTime').hours();
  const startDateMinute = form.getFieldValue('startDateTime') && form.getFieldValue('startDateTime').minutes();


  let disabledTime = {
  };

  if (currentStartDate && currentStartDate.isSame(currentEndDate) && form.getFieldValue('startDateTime').isAfter(moment())) {
    disabledTime = {
      disabledHours: () => range(0, startDateHour),
      disabledMinutes: () => ((form.getFieldValue('endDateTime').hours() === form.getFieldValue('startDateTime').hours()) ? range(0, startDateMinute + 1) : []),
    };
  } else if (currentEndDate && startOfToday.isSame(currentEndDate)) {
    disabledTime = {
      disabledHours: () => range(0, moment().hours()),
      disabledMinutes: () => ((form.getFieldValue('endDateTime').hours() === moment().hours()) ? range(0, moment().minutes()) : []),
    };
  }

  return (
    <Form.Item required label={<FormattedMessage id="until" />}>
      { form.getFieldDecorator('endDateTime', {
        initialValue: notification && notification.endDateTime ? moment(notification.endDateTime) : null,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
          {
            validator(rule, value, callback) {
              if (value && (value.isBefore(moment()) || (form.getFieldValue('startDateTime') ? value.isBefore(form.getFieldValue('startDateTime')) : false))) {
                callback(intl.formatMessage({ id: 'endDateAfterStart' }));
              }
              callback();
            },
          },
        ],
      })(
        <DatePicker
          style={{ width: '300px' }}
          disabled={disabled}
          disabledDate={value => value.isBefore(moment().startOf('d')) || (currentStartDate ? value.isBefore(currentStartDate) : false)}
          disabledTime={() => disabledTime}
          defaultPickerValue={form.getFieldValue('endDate') || form.getFieldValue('startDate')}
          placeholder={intl.formatMessage({ id: 'selectOne' })}
          showToday
          format="DD/MM/YYYY HH:mm"
          showTime={{ format: 'HH:mm' }}
        />,
      )}
    </Form.Item>
  );
});

// MODAL AND FORM
const EditNotification = Form.create({ name: 'edit-notification' })(({
  form, visible, setVisible, notification, onCreate, onUpdate, onCancel, isLoading, isSubmitting, validationBox,
  languages, getAvailiableLanguages, ...rest
}) => {
  const intl = useIntl();
  const isCreating = !notification;
  const titleId = isCreating ? 'create' : 'edit';
  const buttonId = isCreating ? 'create' : 'save';

  const [onlyEditEndDate, setOnlyEditEndDate] = useState(false);

  const { resetFields } = form;

  // Keep messages in different languages
  const [transMessages, setTransMessages] = useState([]);
  const [lang, setLang] = useState('en_GB');

  // Get languages
  useEffect(() => {
    if (!languages) getAvailiableLanguages();
  }, [languages, getAvailiableLanguages]);

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();
      setLang('en_GB');

      // Main message
      const rowMessages = [];
      const mainText = notification ? notification.message : '';

      setOnlyEditEndDate(notification ? moment().isAfter(moment(notification.startDateTime)) : false);

      // Messages for languages
      if (languages && languages.length > 0) {
        const existingTexts = notification && notification.translatedMessages ? notification.translatedMessages : [];
        languages.forEach(lange => {
          let langText = mainText;
          let sortText = 'aaaaaaaaaa';
          if (lange.languageCode !== 'en_GB') {
            const storedText = existingTexts.find(obj => obj.locale === lange.languageCode);
            langText = storedText ? storedText.message : '';
            sortText = lange.languageDescription;
          }
          rowMessages.push({
            locale: lange.languageCode,
            langDesc: lange.languageDescription,
            text: langText,
            sort: sortText,
          });
        });
      }
      setTransMessages(rowMessages);
    }
  }, [resetFields, visible, setTransMessages, notification, languages, setOnlyEditEndDate]);

  // On form submit.
  const submitForm = useCallback(() => {
    form.validateFields((err, values) => {
      if (!err) {
        // main values
        const newNotification = notification
          ? {
            ...notification,
            startDateTime: values.startDateTime,
            endDateTime: values.endDateTime,
            notificationType: values.notificationType,
            message: values.message_en_GB,
          } : {
            notificationType: values.notificationType,
            message: values.message_en_GB,
            startDateTime: values.startDateTime,
            endDateTime: values.endDateTime,
            active: true,
            translatedMessages: [],
          };
        // translations
        languages.forEach(lange => {
          const attrib = `message_${lange.languageCode}`;
          if (values[attrib] && lange.languageCode !== 'en_GB') {
            const idx = newNotification.translatedMessages.findIndex(trans => trans.locale === lange.languageCode);
            if (idx > -1) {
              newNotification.translatedMessages[idx].message = values[attrib];
            } else {
              newNotification.translatedMessages.push({
                translationId: -999,
                notificationId: notification ? notification.notificationId : -999,
                locale: lange.languageCode,
                message: values[attrib],
              });
            }
          }
        });
        // submit
        const submitFunction = isCreating ? onCreate : onUpdate;
        submitFunction(newNotification, intl.formatMessage({ id: 'notification' }));
      }
    });
  }, [form, notification, isCreating, onCreate, onUpdate, intl, languages]);

  return (
    <Modal
      title={(
        <span>
          <FormattedMessage id={titleId} />
          &nbsp;
          <FormattedMessage id="notification" />
        </span>
      )}
      okText={<FormattedMessage id={buttonId} />}
      onOk={submitForm}
      visible={visible}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      destroyOnClose
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
        <ViaValidationAlert validationError={validationBox} disabled={onlyEditEndDate} />
        <TypeField form={form} notification={notification} disabled={onlyEditEndDate} />
        <Form.Item required label={<FormattedMessage id="message" />} style={{ marginBottom: 0 }}>
          <Radio.Group
            size="small"
            value={lang}
            onChange={e => {
              setLang(e.target.value);
            }}
          >
            { transMessages ? transMessages.sort((rowMsg1, rowMsg2) => `${rowMsg1.sort}`.localeCompare(`${rowMsg2.sort}`)).map(transMsg => (
              <Radio.Button value={transMsg.locale} key={Math.random()} style={{ marginBottom: '0.2em' }}>
                <Row type="flex" align="middle">
                  <ReactCountryFlag code={transMsg.locale.slice(transMsg.locale.indexOf('_') + 1, transMsg.locale.length).toLowerCase()} svg />
                  <Text>
                      &nbsp;
                    {transMsg.langDesc}
                  </Text>
                  {transMsg.locale === 'en_GB'
                    ? <Text style={{ color: 'red' }}>*</Text>
                    : ''}
                </Row>
              </Radio.Button>
            ))
              : '' }
          </Radio.Group>
          { transMessages.map(msg => (
            <MessageField form={form} text={msg.text} locale={msg.locale} showLocale={lang} key={msg.locale} disabled={onlyEditEndDate} />
          )) }
        </Form.Item>
        <StartDateField form={form} notification={notification} disabled={onlyEditEndDate} />
        <EndDateField
          form={form}
          notification={notification}
          disabled={notification && notification.endDateTime ? moment().isAfter(moment(notification.endDateTime)) : false}
        />
      </Form>
    </Modal>
  );
});

// Validate the prop type parameters.
EditNotification.propTypes = {
  visible: PropTypes.bool.isRequired,
  notification: PropTypes.oneOfType([PropTypes.object]),
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  languages: PropTypes.arrayOf(PropTypes.object),
  getAvailiableLanguages: PropTypes.func.isRequired,
};

EditNotification.defaultProps = {
  notification: null,
  isLoading: false,
  validationBox: null,
  languages: null,
};

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectSubmitNotificationsRequest(state);
  const pageDataRequest = selectNotificationsPageDataRequest(state);

  return {
    visible: editState.isEditing,
    notification: editState.notificationId ? selectNotification(state, editState.notificationId) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
    languages: selectAppConfig(state, 'languages') ? selectAppConfig(state, 'languages').languages : null,
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: (notification, label) => dispatch(requestAddNotification(notification, label)),
  onUpdate: (notification, label) => dispatch(requestUpdateNotification(notification, label)),
  onCancel: () => dispatch(cancelEditNotification()),
  getAvailiableLanguages: () => dispatch(requestSupportedLanguages()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditNotification);
